import { Component, ViewEncapsulation, input, computed, inject, InjectionToken, Type } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { OverlineComponent } from '../../atoms/overline/overline';
import { STRAPI_CONFIG } from '../../../core/api/strapi.config';

export type DynamicSectionBackground = '' | 'white' | 'seashell' | 'dark';

/** Injection token to break circular dependency with component-registry */
export interface BlockRegistryEntry {
  component: Type<unknown>;
  adapter: (entry: Record<string, unknown>, baseUrl: string) => Record<string, unknown>;
}

export const BLOCK_REGISTRY = new InjectionToken<Record<string, BlockRegistryEntry>>('BlockRegistry');

@Component({
  selector: 'ds-dynamic-section',
  templateUrl: './dynamic-section.html',
  styleUrl: './dynamic-section.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [OverlineComponent, NgComponentOutlet],
})
export class DynamicSectionComponent {
  readonly sectionId = input<string>('');
  readonly background = input<DynamicSectionBackground>('');
  readonly overline = input<string>('');
  readonly heading = input<string>('');
  readonly subtitle = input<string>('');
  readonly blocks = input<Record<string, unknown>[]>([]);
  readonly linkText = input<string>('');
  readonly linkHref = input<string>('');
  readonly cssClass = input<string>('');

  private readonly config = inject(STRAPI_CONFIG);
  private readonly registry = inject(BLOCK_REGISTRY, { optional: true });

  readonly resolvedBlocks = computed(() => {
    const reg = this.registry;
    if (!reg || this.blocks().length === 0) return [];

    const baseUrl = this.config.baseUrl;
    return this.blocks()
      .filter(b => b['__component'])
      .map((b, i) => {
        const name = b['__component'] as string;
        const entry = reg[name];
        if (!entry) return null;
        try {
          return {
            id: `${name}-${i}`,
            component: entry.component,
            inputs: entry.adapter(b, baseUrl),
          };
        } catch { return null; }
      })
      .filter((b): b is NonNullable<typeof b> => b !== null);
  });
}
